import LoginDesignPanel, { LoginDesignVariant } from "./LoginDesignPanel";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { isSilentRecoveryOutcome } from "../utils/authExpiryGuard";
import { useNavigate } from "react-router-dom";
import logoBranca from "../assets/maritaca_logo_branca.png";
import { reportApiError, reportHandledError } from "../utils/reportError";

const matchesEmailVerificationWarning = (text?: string | null) => {
  if (!text) return false;
  const normalized = text.toLowerCase().replace(/-/g, "");
  return /confirm|verif/.test(normalized) && /e?mails?/.test(normalized);
};

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z" />
      <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2.04a4.8 4.8 0 0 1-2.7.75 4.8 4.8 0 0 1-4.52-3.36H1.83v2.07A8 8 0 0 0 8.98 17z" />
      <path fill="#FBBC05" d="M4.46 10.41A4.8 4.8 0 0 1 4.21 9c0-.49.09-.97.25-1.41V5.52H1.83a8 8 0 0 0 0 7.16l2.63-2.27z" />
      <path fill="#EA4335" d="M8.98 3.58c1.32 0 2.5.45 3.44 1.35l2.54-2.54A8 8 0 0 0 8.98 0a8 8 0 0 0-7.15 4.42l2.63 2.27c.63-1.89 2.39-3.11 4.52-3.11z" />
    </svg>
  );
}

export default function Login({ previewVariant }: { previewVariant?: LoginDesignVariant } = {}) {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
  const alignHeadings = !previewVariant || ["atual", "editorial-verde-escuro", "radial-suave", "radial-original"].includes(previewVariant);
  const layoutRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const layout = layoutRef.current;
    const form = layout?.querySelector<HTMLElement>('[data-testid="platform-login-panel"] > .mx-auto');
    const heading = form?.querySelector('[data-testid=login-account-label]');
    if (!layout || !form || !heading || !alignHeadings) return;
    const align = () => layout.style.setProperty('--login-heading-top',
      (heading.getBoundingClientRect().top - layout.getBoundingClientRect().top) + 'px');
    const observer = new ResizeObserver(align);
    observer.observe(form);
    observer.observe(layout);
    align();
    document.fonts.ready.then(() => { if (layout.isConnected) align(); });
    return () => observer.disconnect();
  }, [previewVariant, alignHeadings]);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [showResendButton, setShowResendButton] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const urlError = new URLSearchParams(window.location.search).get("error");
  const urlErrorDescription = new URLSearchParams(window.location.search).get("error_description");
  const hasAuthErrorParams = Boolean(urlError || urlErrorDescription);
  const isEmailVerificationAlert = matchesEmailVerificationWarning(alertMessage);

  useEffect(() => {
    if (previewVariant || !urlError) return;
    if (urlErrorDescription === "account_linked") {
      const url = new URL(window.location.href);
      url.searchParams.delete("error");
      url.searchParams.delete("error_description");
      window.history.replaceState({}, document.title, url.toString());
      // O deny do account_linked NÃO deixa sessão SSO no Auth0; sem a connection
      // original o retry cai numa segunda tela de login. Repetindo a connection,
      // o retry re-federa no IdP (que tem sessão) e completa sem interação.
      const lastConnection = sessionStorage.getItem("auth0_last_connection") || undefined;
      const lastHint = sessionStorage.getItem("auth0_last_login_hint") || undefined;
      loginWithRedirect({
        authorizationParams: {
          scope: "openid profile email offline_access",
          redirect_uri: window.location.origin,
          connection: lastConnection,
          login_hint: lastHint,
        },
        appState: { returnTo: "/" },
      });
      return;
    }

    if (isSilentRecoveryOutcome(urlError)) {
      // Outcome of AuthExpiryGuard's prompt=none recovery: the Auth0 session
      // is gone too, so the user really has to sign in again. That is the
      // normal login screen, not an error to alarm them with.
      const url = new URL(window.location.href);
      url.searchParams.delete("error");
      url.searchParams.delete("error_description");
      url.searchParams.delete("state");
      window.history.replaceState({}, document.title, url.toString());
      setLoading(false);
      setIsSignupMode(false);
      return;
    }

    setAlertMessage(urlErrorDescription || "Erro de autenticação");
    setShowResendButton(matchesEmailVerificationWarning(urlErrorDescription));
    const url = new URL(window.location.href);
    url.searchParams.delete("error");
    url.searchParams.delete("error_description");
    window.history.replaceState({}, document.title, url.toString());
    setLoading(false);
    setIsSignupMode(false);
  }, [previewVariant, urlError, urlErrorDescription, loginWithRedirect]);

  useEffect(() => {
    if (previewVariant || hasAuthErrorParams || isEmailVerificationAlert) return;
    if (!isLoading && isAuthenticated) navigate("/", { replace: true });
  }, [previewVariant, hasAuthErrorParams, isEmailVerificationAlert, isLoading, isAuthenticated, navigate]);

  const handleLogin = async () => {
    setLoading(true);
    setAlertMessage(null);
    setIsSignupMode(false);
    try {
      sessionStorage.setItem("auth0_last_connection", "Username-Password-Authentication");
      if (email) sessionStorage.setItem("auth0_last_login_hint", email);
      await loginWithRedirect({
        authorizationParams: {
          connection: "Username-Password-Authentication",
          scope: "openid profile email",
          screen_hint: "login",
          login_hint: email || undefined,
          redirect_uri: window.location.origin,
          prompt: "login",
        },
        appState: { returnTo: "/", mode: "login" },
      });
    } catch {
      setAlertMessage("Erro ao fazer login. Tente novamente.");
      setLoading(false);
    }
  };

  const handleSocialLogin = async () => {
    setAlertMessage(null);
    try {
      sessionStorage.setItem("auth0_last_connection", "google-oauth2");
      sessionStorage.removeItem("auth0_last_login_hint");
      await loginWithRedirect({
        authorizationParams: {
          connection: "google-oauth2",
          scope: "openid profile email",
          redirect_uri: window.location.origin,
          prompt: "login",
        },
        appState: { returnTo: "/" },
      });
    } catch {
      setAlertMessage("Erro ao fazer login social");
    }
  };

  const goToSignupDirect = () => {
    setLoading(true);
    setAlertMessage(null);
    setIsSignupMode(true);
    const domain = process.env.REACT_APP_AUTH0_DOMAIN;
    const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
    const redirectUri = encodeURIComponent(window.location.origin);
    const scope = encodeURIComponent("openid profile email");
    window.location.href =
      `https://${domain}/authorize?response_type=code&client_id=${clientId}` +
      `&redirect_uri=${redirectUri}&scope=${scope}&screen_hint=signup&prompt=login&initial_screen=signUp` +
      (email ? `&login_hint=${encodeURIComponent(email)}` : "");
  };

  const handleResendVerification = async () => {
    if (!email) {
      setAlertMessage("Digite seu email acima para receber um novo link.");
      return;
    }
    setResendLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_AUTH0_AUDIENCE_URL}/auth/resend-verification`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );
      if (response.ok) {
        setAlertMessage("Email de verificação reenviado. Confira sua caixa de entrada.");
        setShowResendButton(false);
      } else {
        reportApiError(response, { feature: "login.resend_verification", method: "POST" });
        const errorData = await response.json();
        setAlertMessage(errorData.detail || "Não foi possível reenviar o email de verificação.");
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "login.resend_verification" });
      setAlertMessage("Não foi possível reenviar o email. Tente novamente.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <main ref={layoutRef} className={(previewVariant ? "" : "platform-login-live ") + (alignHeadings ? "platform-login-aligned " : "") + "grid min-h-[100dvh] bg-white font-sans text-[#15372f] lg:grid-cols-[minmax(520px,1fr)_minmax(460px,.9fr)]"}>
      <LoginDesignPanel variant={previewVariant && previewVariant !== "atual" ? previewVariant : "radial-suave"} />

      <section data-testid="platform-login-panel" className="relative flex min-h-[100dvh] flex-col justify-center bg-[#f2f1f1] px-[clamp(28px,5vw,72px)] py-7 max-lg:justify-start max-lg:bg-[#1a3c34] max-lg:pt-5">
        <div className="mb-6 hidden items-center gap-2.5 text-white max-lg:flex">
          <img src={logoBranca} alt="" className="h-8 w-8 object-contain" />
          <span className="text-xl font-bold tracking-[-.04em]">maritaca</span>
        </div>

        <div className="mx-auto w-full max-w-[410px] max-lg:my-auto max-lg:rounded-[22px] max-lg:bg-[#f2f1f1] max-lg:px-5 max-lg:py-6">
          <p data-testid="login-account-label" className="mb-3 text-[10px] font-extrabold uppercase tracking-[.14em] text-[#ff7f40]">Sua conta Maritaca</p>
          <h2 className="text-[clamp(29px,2.65vw,38px)] font-medium leading-[1.05] tracking-[-.04em]">Entre na Plataforma Maritaca</h2>
          <p className="mb-7 mt-3 max-w-[350px] text-[13px] leading-relaxed text-[#617069]">Gerencie suas chaves, acompanhe o uso da API e consulte seus créditos.</p>

          {alertMessage && (
            <div role="alert" className="mb-4 border-l-[3px] border-[#ff7f40] bg-[#fff2e8] p-3 text-[12px] leading-relaxed text-[#73351f]">
              <div>{alertMessage}</div>
              {showResendButton && (
                <button type="button" onClick={handleResendVerification} disabled={resendLoading} className="mt-2 font-bold text-[#1a3c34] underline underline-offset-2 disabled:opacity-60">
                  {resendLoading ? "Reenviando…" : "Reenviar email de verificação"}
                </button>
              )}
            </div>
          )}

          <form onSubmit={(event) => { event.preventDefault(); handleLogin(); }} className="flex flex-col">
            <label htmlFor="platform-email" className="mb-2 text-[12px] font-bold">Seu email</label>
            <input id="platform-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="voce@empresa.com.br" autoComplete="email" required autoFocus className="h-[52px] w-full rounded-full border border-[#b8c0bb] bg-white px-5 text-[14px] outline-none transition focus:border-[#1a3c34] focus:ring-4 focus:ring-[#1a3c34]/10" />
            <button type="submit" disabled={loading} className="relative mt-2.5 h-[52px] w-full rounded-full border border-[#ff7f40] bg-[#ff7f40] text-[14px] font-bold text-white transition hover:bg-[#ed653b] disabled:cursor-wait disabled:opacity-60">
              {loading && !isSignupMode ? "Abrindo…" : "Continuar com email"}
              <span aria-hidden="true" className="absolute right-5 text-lg">→</span>
            </button>

            <div className="my-4 flex items-center gap-3 text-[10px] uppercase text-[#8c958f] before:h-px before:flex-1 before:bg-[#d8dcd9] after:h-px after:flex-1 after:bg-[#d8dcd9]">ou</div>
            <button type="button" onClick={handleSocialLogin} disabled={loading} className="flex h-[52px] w-full items-center justify-center gap-2.5 rounded-full border border-[#ccd3ce] bg-transparent text-[14px] font-bold transition hover:bg-white/60 disabled:opacity-60">
              <GoogleMark /> Continuar com Google
            </button>
          </form>

          <p className="mt-6 text-center text-[12.5px] text-[#637069]">
            Primeira vez por aqui?{" "}
            <button type="button" onClick={goToSignupDirect} disabled={loading} className="font-extrabold text-[#1a3c34] underline underline-offset-4">
              {loading && isSignupMode ? "Abrindo…" : "Crie sua conta"}
            </button>
          </p>
          <p className="mx-auto mt-5 text-center text-[10px] leading-relaxed text-[#848981]">
            Ao continuar, você concorda com os{" "}
            <a href="https://www.maritaca.ai/termos-e-condicoes" target="_blank" rel="noreferrer" className="underline">Termos</a> e a{" "}
            <a href="https://www.maritaca.ai/politica-de-privacidade" target="_blank" rel="noreferrer" className="underline">Privacidade</a>.
          </p>
        </div>

        <div className="absolute bottom-5 right-7 flex items-center gap-4 text-[11px] max-lg:static max-lg:mx-auto max-lg:mt-5 max-lg:text-white/70">
          <a href="https://docs.maritaca.ai" target="_blank" rel="noreferrer" className="border-b border-[#a9aea8] text-[#68736d] no-underline max-lg:text-white/70">Documentação</a>
          <a href="mailto:suporte@maritaca.ai" className="inline-flex items-center gap-1.5 rounded-full border border-[#a9aea8] px-3 py-1.5 font-bold text-[#53635c] no-underline transition hover:border-[#1a3c34] hover:text-[#1a3c34] max-lg:border-white/40 max-lg:text-white/80">
            <span aria-hidden="true" className="flex h-4 w-4 items-center justify-center rounded-full border border-current text-[9px]">?</span>
            Precisa de ajuda?
          </a>
        </div>
      </section>
    </main>
  );
}
